<script setup lang="ts">
import { useCompanyStore } from '@/shared/stores/CompanyStore'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { storeToRefs } from 'pinia'
import CreateCompanyFormDialog from '@/features/companies/dialogs/CreateCompanyFormDialog.vue'
import { useConfirm } from 'primevue/useconfirm'
import {
  useListAdminCompanyActiveSubscriptions,
  useListAdminMatchingJobCategories,
  useListAdminMatchingJobFunctions,
  useListAdminUsers,
  useListSegments,
  useSearchAdminCompanies,
} from '@swipe4work/api-orval'
import {
  ActiveSinceFilter,
  AdminPermissionResource,
  CompanyDisplayType,
  CompanyType,
  CreateCompanyFromAdminCreationType,
  PermissionLevel,
  Province,
  type SearchCompaniesFromAdminCompanyListItemDto,
  SegmentType,
} from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { getLeadStatus } from '@/shared/adapters/UseCompanyData'
import { useCompanyActions } from '@/features/companies/composables/useCompanyActions'
import { buildRenewalRangePresets } from '@/shared/utils/dateRangePresets'
import type { DateRangeValue } from '@/shared/composables/UseDataTableFilters'

definePage({
  path: '/companies',
  name: 'companies.overview',
  meta: {
    title: 'Bedrijven overzicht',
    permission: {
      resource: 'Company',
      level: 'Read',
    },
  },
})

const route = useRoute()
const router = useRouter()
const companyStore = useCompanyStore()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const provinceOptions = Object.values(Province).map(p => ({
  label: p,
  value: p, 
}))
const companyActions = useCompanyActions()

const { can } = usePermissions()
const canCompanyWrite = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Write))
const canCompanyDelete = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Delete))
const canCompanyMediaWrite = computed(() => can(AdminPermissionResource.CompanyMedia, PermissionLevel.Write))

onBeforeMount(() => {
  if (route.query.redirect === 'home') {
    router.push({
      name: 'companies.overview',
      query: {},
    })
  }
})

type FilterState = {
  segmentId: number | null
  activeSince: ActiveSinceFilter | null
  accountManagerId: number | null
  salesPersonId: number | null
  jobCategoryIds: number[]
  jobFunctionIds: number[]
  provinces: Province[]
  displayTypes: CompanyDisplayType[]
  subscriptions: string[]
  hasPublishedJobs: boolean | null
  isIdeal: boolean | null
  hasIdealJobs: boolean | null
  nextRenewalRange: DateRangeValue | null
}

const {
  filters,
  draft,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
  onSort,
  onPage,
} = useDataTableFilters<FilterState>({
  schema: {
    segmentId: 'id',
    activeSince: 'enum',
    accountManagerId: 'id',
    salesPersonId: 'id',
    jobCategoryIds: 'ids',
    jobFunctionIds: 'ids',
    provinces: 'enums',
    displayTypes: 'enums',
    subscriptions: 'enums',
    hasPublishedJobs: 'boolean',
    isIdeal: 'boolean',
    hasIdealJobs: 'boolean',
    nextRenewalRange: 'dateRange',
  },
  defaultFilters: () => ({
    segmentId: null,
    activeSince: null,
    accountManagerId: null,
    salesPersonId: null,
    jobCategoryIds: [],
    jobFunctionIds: [],
    provinces: [],
    displayTypes: [],
    subscriptions: [],
    hasPublishedJobs: null,
    isIdeal: null,
    hasIdealJobs: null,
    nextRenewalRange: null,
  }),
  persistToStorage: false,
  defaultSortField: 'createdAt',
  defaultSortDescending: true,
})

// Reference data
const { data: jobCategoriesData } = useListAdminMatchingJobCategories({ query: { staleTime: Infinity } })
const { data: jobFunctionsData } = useListAdminMatchingJobFunctions({ query: { staleTime: Infinity } })
const { data: adminUsersData } = useListAdminUsers({ query: { staleTime: 5 * 60 * 1000 } })
const { data: segmentsData } = useListSegments(
  { type: SegmentType.Company },
  { query: { staleTime: 5 * 60 * 1000 } },
)

const jobCategoryOptions = computed(() => jobCategoriesData.value?.jobCategories ?? [])
const jobFunctionOptions = computed(() => jobFunctionsData.value?.jobFunctions ?? [])
const adminUsers = computed(() => adminUsersData.value?.users ?? [])

const jobCategoryNameById = computed(() =>
  Object.fromEntries(jobCategoryOptions.value.map(c => [ c.id, c.name ])),
)

const currentAdminUserId = computed(() => {
  if (!user.value?.localAccountId) {
    return null
  }

  return adminUsers.value.find(u => u.externalId === user.value?.localAccountId)?.id ?? null
})

const companySegmentOptions = computed(() => {
  const allSegments = segmentsData.value?.segments ?? []

  return allSegments.filter(s => s.isPublic || s.authorEmployerId === currentAdminUserId.value)
})

const accountManagerOptions = computed(() =>
  adminUsers.value.map(u => ({
    id: u.id,
    label: u.name,
  })),
)

// Server-side query
const params = computed(() => ({
  search: search.value,
  segmentId: filters.segmentId ?? undefined,
  activeSince: filters.activeSince ?? undefined,
  accountManagerId: filters.accountManagerId ?? undefined,
  salesPersonId: filters.salesPersonId ?? undefined,
  jobCategoryIds: filters.jobCategoryIds.length > 0 ? filters.jobCategoryIds : undefined,
  jobFunctionIds: filters.jobFunctionIds.length > 0 ? filters.jobFunctionIds : undefined,
  provinces: filters.provinces.length > 0 ? filters.provinces : undefined,
  displayTypes: filters.displayTypes.length > 0 ? filters.displayTypes : undefined,
  subscriptions: filters.subscriptions.length > 0 ? filters.subscriptions : undefined,
  hasPublishedJobs: filters.hasPublishedJobs ?? undefined,
  isIdeal: filters.isIdeal ?? undefined,
  hasIdealJobs: filters.hasIdealJobs ?? undefined,
  nextRenewalFromDate: filters.nextRenewalRange?.from ?? undefined,
  nextRenewalToDate: filters.nextRenewalRange?.to ?? undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value ? true : undefined,
  Limit: rows.value,
  Offset: offset.value,
}))

const renewalPresets = computed(() => buildRenewalRangePresets())

const { data, isPending, isError } = useSearchAdminCompanies(params)

const results = computed(() => data.value?.companies ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)

const { data: activeSubscriptionsData } = useListAdminCompanyActiveSubscriptions({
  query: { staleTime: 5 * 60 * 1000 },
})

const subscriptionOptions = computed(() =>
  (activeSubscriptionsData.value?.subscriptions ?? []).map(title => ({
    label: title,
    value: title,
  })),
)

const confirm = useConfirm()
const menu = ref()
const selectedCompany = ref<SearchCompaniesFromAdminCompanyListItemDto | null>(null)

/*
 * Internal staff see every company here, but editing one and logging in to its portal stay bound to
 * holding an employer role on that company (`canManage`).
 */
const canManageSelected = computed(() => selectedCompany.value?.canManage === true)

const menuItems = computed(() => [
  {
    label: 'Bekijk',
    icon: 'fa-regular fa-info-circle',
    command: () => router.push({
      name: 'companies.[id]',
      params: { id: selectedCompany.value!.id },
    }),
  },
  { separator: true },
  {
    label: 'Login in portaal',
    icon: canManageSelected.value ? 'fa-regular fa-sign-in-alt' : 'fa-regular fa-lock',
    disabled: !canManageSelected.value,
    command: () => companyActions.loginToPortal(selectedCompany.value!.id),
  },
  {
    label: 'Naar bedrijf in Teamleader',
    icon: 'fa-regular fa-arrow-up-right-from-square',
    visible: () => Boolean(selectedCompany.value?.teamleaderCompanyId),
    command: () => companyActions.openInTeamleader(selectedCompany.value?.teamleaderCompanyId),
  },
  {
    label: 'Markeer als behandeld',
    icon: canCompanyWrite.value ? 'fa-regular fa-check' : 'fa-regular fa-lock',
    disabled: !canCompanyWrite.value,
    visible: () => selectedCompany.value?.type === CompanyType.Lead && !selectedCompany.value?.statistics.accountManagerFollowedUpAt,
    command: async () => await companyStore.markFollowUp(selectedCompany.value!),
  },
  { separator: true },
  {
    label: 'Wijzig bedrijf',
    icon: canCompanyWrite.value && canManageSelected.value ? 'fa-regular fa-edit' : 'fa-regular fa-lock',
    disabled: !canCompanyWrite.value || !canManageSelected.value,
    command: () => companyActions.openUpdateDialog(selectedCompany.value!.id),
  },
  {
    label: 'Wijzig bedrijfsvideo',
    icon: canCompanyMediaWrite.value ? 'fa-regular fa-film' : 'fa-regular fa-lock',
    disabled: !canCompanyMediaWrite.value,
    command: () => companyActions.openUpdateVideoDialog(selectedCompany.value!.id),
  },
  {
    label: 'Verwijderen',
    icon: canCompanyDelete.value ? 'fa-regular fa-trash-alt' : 'fa-regular fa-lock',
    disabled: !canCompanyDelete.value,
    command: async () => await companyStore.deleteCompany(selectedCompany.value!.id, confirm),
  },
])

function handleClickRow(event: any, company: SearchCompaniesFromAdminCompanyListItemDto) {
  selectedCompany.value = company
  menu.value.show(event.originalEvent)
}

function getEffectiveCompanyPlan(company: SearchCompaniesFromAdminCompanyListItemDto) {
  if (company.parentCompanyPlan) {
    return company.parentCompanyPlan
  }

  return company.plan
}

type CompanyTypeInfo = {
  title: string
  detail: string
  severity: 'secondary' | 'info' | 'success' | 'purple'
  subType?: {
    title: string;
    severity: 'info' | 'success' 
  }
}

function getEffectivePlanType(company: SearchCompaniesFromAdminCompanyListItemDto): CompanyTypeInfo {
  const plan = getEffectiveCompanyPlan(company)

  if (plan?.activeSubscription || Number.isFinite(plan?.assignedJobSlots)) {
    return {
      title: 'Vacatureslots',
      detail: plan?.activeSubscription?.title
        ?? (Number.isFinite(plan?.assignedJobSlots)
          ? `Overschreven limiet ${plan?.assignedJobSlots} slots`
          : '—'),
      severity: 'info' as const,
    }
  }

  return {
    title: 'Match-Credits',
    detail: `${plan?.matchCredits ?? 0} credits`,
    severity: 'success' as const,
  }
}

function getCompanyType(company: SearchCompaniesFromAdminCompanyListItemDto): CompanyTypeInfo {
  if (company.type === CompanyType.Lead) {
    return {
      title: 'Lead',
      detail: getLeadStatus(company.status.state)?.title ?? '—',
      severity: 'secondary' as const,
    }
  }

  if (company.parentCompany) {
    const effectiveType = getEffectivePlanType(company)

    return {
      title: 'Subbedrijf',
      detail: company.parentCompany.name,
      severity: 'purple' as const,
      subType: {
        title: effectiveType.title,
        severity: effectiveType.severity as 'info' | 'success', 
      },
    }
  }

  return getEffectivePlanType(company)
}

function getCompanyTypeDetail(company: SearchCompaniesFromAdminCompanyListItemDto) {
  return getCompanyType(company).detail
}

const dialog = useDynamicDialog()

function createCompany() {
  dialog.open(CreateCompanyFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
    },
    data: {
      creationType: CreateCompanyFromAdminCreationType.MatchCredits,
    },
  })
}
</script>

<template>
  <div class="space-y-5">
    <ContextMenu
      ref="menu"
      :model="menuItems"
      @hide="selectedCompany = null"
    />

    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <h3 class="text-lg font-semibold">
              Bedrijven
            </h3>
            <div class="flex items-center gap-3">
              <DataTableFilterPanel
                :active-filter-count="activeFilterCount"
                @show="onFilterPanelShow"
                @apply="onFilterApply"
                @clear="onFilterClear"
              >
                <CompanyFilters
                  :draft="draft"
                  :company-segment-options="companySegmentOptions"
                  :subscription-options="subscriptionOptions"
                  :account-manager-options="accountManagerOptions"
                  :job-category-options="jobCategoryOptions"
                  :job-function-options="jobFunctionOptions"
                  :job-category-name-by-id="jobCategoryNameById"
                  :province-options="provinceOptions"
                  :renewal-presets="renewalPresets"
                />
              </DataTableFilterPanel>

              <IconField class="max-w-xs flex-1">
                <InputIcon class="fa-regular fa-search" />
                <InputText
                  v-model="searchInput"
                  placeholder="Zoeken op naam..."
                  class="w-full"
                />
                <InputIcon
                  v-if="searchInput"
                  class="fa-regular fa-xmark cursor-pointer"
                  style="pointer-events: auto"
                  @click="searchInput = ''"
                />
              </IconField>

              <Button
                v-permission="{ resource: AdminPermissionResource.Company, level: PermissionLevel.Write }"
                label="Nieuw bedrijf"
                icon="fa-regular fa-plus"
                @click="createCompany"
              />
            </div>
          </div>

          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton
              v-for="n in 8"
              :key="n"
              height="3rem"
            />
          </div>
          <Message
            v-else-if="isError"
            severity="error"
          >
            Er is iets misgegaan bij het laden van bedrijven.
          </Message>
          <div
            v-else
            class="overflow-x-auto"
          >
            <DataTable
              :value="results"
              :lazy="true"
              :first="offset"
              :rows="rows"
              :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
              :total-records="totalCount"
              :sort-field="sortField"
              :sort-order="sortDescending ? -1 : 1"
              paginator
              paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              current-page-report-template="{first}-{last} van {totalRecords}"
              size="small"
              class="w-full"
              row-hover
              @row-click="(e) => handleClickRow(e, e.data)"
              @page="onPage"
              @sort="onSort"
            >
              <Column
                field="name"
                header="Bedrijf"
                sortable
                style="min-width: 200px"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <div class="flex items-center gap-2">
                    <div>
                      <Avatar
                        v-if="company.logoUrl"
                        :image="company.logoUrl"
                        shape="circle"
                        :pt="{
                          image: {
                            // @unocss-include
                            class: 'object-contain',
                          },
                        }"
                      />
                      <Avatar
                        v-else
                        shape="circle"
                        :label="company.name.charAt(0)"
                      />
                    </div>
                    <div class="max-w-60 flex flex-col">
                      <h3 class="flex items-center gap-1 font-semibold">
                        <span v-tooltip.right="company.name" class="line-clamp-1">{{ company.name }}</span>
                        <i
                          v-if="company.isIdeal"
                          v-tooltip.top="'Ideal Company'"
                          class="fa-solid fa-star shrink-0 text-xs text-primary"
                        />
                      </h3>
                      <span
                        v-if="company.parentCompany"
                        class="line-clamp-1 text-xs text-surface-400"
                      >
                        {{ company.parentCompany.name }}
                      </span>
                      <h4 class="line-clamp-1 truncate text-sm">
                        {{ company.location.name }} &#8212; {{ company.location?.province }}
                      </h4>
                    </div>
                  </div>
                </template>
              </Column>
              <Column
                header="Type"
                style="width: 11rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <div class="flex flex-col items-start gap-1">
                    <PillTag
                      class="w-fit"
                      :value="getCompanyType(company).title"
                      :severity="getCompanyType(company).severity"
                    />
                    <PillTag
                      v-if="getCompanyType(company).subType"
                      class="w-fit"
                      :value="getCompanyType(company).subType!.title"
                      :severity="getCompanyType(company).subType!.severity"
                    />
                    <span
                      v-if="company.subscriptionStartsOn"
                      v-tooltip.top="'Abonnement nog niet gestart'"
                      class="text-xs text-warning-600"
                    >
                      <i class="fa-regular fa-clock mr-1" />
                      start {{ new Date(company.subscriptionStartsOn).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' }) }}
                    </span>
                    <span
                      v-if="company.nextRenewalDate"
                      v-tooltip.top="'Datum van volgende hernieuwing'"
                      class="text-xs text-surface-500"
                    >
                      <i class="fa-regular fa-rotate-right mr-1" />
                      {{ new Date(company.nextRenewalDate).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit' }) }}
                    </span>
                  </div>
                </template>
              </Column>
              <Column
                header="Type info"
                style="width: 8rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <span class="text-sm">
                    {{ getCompanyTypeDetail(company) }}
                  </span>
                </template>
              </Column>
              <Column
                header="Beroepsgroepen"
                style="width: 16rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <JobCategoriesWithFunctionsList
                    :categories="company.jobCategoriesWithFunctions"
                    dense
                    empty-label="—"
                  />
                </template>
              </Column>
              <Column
                header="Customer success"
                style="width: 10rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <PersonCell v-if="company.accountManager" :name="company.accountManager.name" />
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
              <Column
                header="Sales persoon"
                style="width: 10rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <PersonCell v-if="company.salesPerson" :name="company.salesPerson.name" />
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
              <Column
                header="Contactpersoon"
                style="min-width: 10rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <PersonCell v-if="company.mainContactPerson?.name" :name="company.mainContactPerson.name" full-name />
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
              <DateColumn
                key="createdAt"
                header="Actief sinds"
                field="createdAt"
                sortable
                style="min-width: 9rem"
                format="DD-MM-YY"
              />
            </DataTable>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
